import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { estimatesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { TruncatedTextWithTooltip } from '../components/TruncatedTextWithTooltip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ListPagination } from '../components/ListPagination';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Search, Loader2, FileText, Filter, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { MobileFAB } from '../components/MobileFAB';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  { value: 'unscheduled', label: 'Unscheduled', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { value: 'sent', label: 'Sent', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { value: 'presented', label: 'Presented', color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  { value: 'awaiting_approval', label: 'Awaiting Approval', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  { value: 'expired', label: 'Expired', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  { value: 'copied_to_job', label: 'Converted', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  return <Badge className={`${opt.color} text-[10px]`}>{opt.label}</Badge>;
}

export default function Estimates() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [estimates, setEstimates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sort') || null;
  const sortDir = searchParams.get('dir') || null;
  const pageSize = 25;

  const handleSort = useCallback((key) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      const curKey = p.get('sort'); const curDir = p.get('dir');
      const DEFAULTS = { number: 'asc', status: 'asc', total: 'desc', created: 'desc' };
      const defDir = DEFAULTS[key] || 'asc';
      if (curKey !== key) { p.set('sort', key); p.set('dir', defDir); }
      else if (curDir === defDir) { p.set('sort', key); p.set('dir', defDir === 'asc' ? 'desc' : 'asc'); }
      else { p.delete('sort'); p.delete('dir'); }
      p.set('page', '1');
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  const fetchEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (sortBy) { params.sort_by = sortBy; params.sort_dir = sortDir; }
      const res = await estimatesAPI.list(params);
      setEstimates(res.data.estimates);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load estimates');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sortBy, sortDir]);

  useEffect(() => { fetchEstimates(); }, [fetchEstimates]);

  const handleSearch = (value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set('search', value); else p.delete('search');
    p.set('page', '1');
    setSearchParams(p);
  };

  const handleStatusFilter = (value) => {
    const p = new URLSearchParams(searchParams);
    if (value && value !== 'all') p.set('status', value); else p.delete('status');
    p.set('page', '1');
    setSearchParams(p);
  };

  const handleDeleteClick = (e, est) => {
    e.stopPropagation();
    if (est.converted_job_id) {
      toast.error('Cannot delete — this estimate has been converted to a job.');
      return;
    }
    setDeleteTarget(est);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await estimatesAPI.remove(deleteTarget.id);
      setEstimates(prev => prev.filter(e => e.id !== deleteTarget.id));
      setTotal(prev => prev - 1);
      toast.success(`Estimate ${deleteTarget.estimate_number} deleted`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete estimate';
      toast.error(msg);
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Layout>
      <div className="space-y-6" data-testid="estimates-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="estimates-title">
              <FileText className="w-6 h-6" />
              Estimates
            </h1>
            <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
              {total} estimate{total !== 1 ? 's' : ''} — create, manage, and convert to jobs
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button onClick={() => navigate('/estimates/new')} data-testid="create-estimate-btn" className="hidden md:inline-flex">
                <Plus className="w-4 h-4 mr-2" /> New Estimate
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search estimates..." value={search} onChange={(e) => handleSearch(e.target.value)} className="pl-10 h-10" data-testid="estimate-search" />
          </div>
          {/* Mobile: filter icon */}
          <div className="sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" data-testid="estimate-mobile-filter-btn">
                  <Filter className={`w-4 h-4 ${statusFilter ? 'text-primary' : ''}`} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleStatusFilter('all')} className={!statusFilter ? 'font-semibold' : ''}>All Statuses</DropdownMenuItem>
                {STATUS_OPTIONS.map(s => (
                  <DropdownMenuItem key={s.value} onClick={() => handleStatusFilter(s.value)} className={statusFilter === s.value ? 'font-semibold' : ''}>{s.label}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Desktop: full dropdown */}
          <div className="hidden sm:block">
            <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-[180px] h-10" data-testid="filter-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-2" data-testid="estimates-mobile-cards">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : estimates.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12">
              <FileText className="w-10 h-10 text-muted-foreground/40" />
              <p className="text-sm font-medium">{search || statusFilter ? 'No estimates found' : 'No estimates yet'}</p>
            </div>
          ) : estimates.map(est => (
            <div
              key={est.id}
              className="border rounded-lg p-3.5 active:bg-muted/50"
              onClick={() => navigate(`/estimates/${est.id}`)}
              data-testid={`estimate-card-${est.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-sm">{est.estimate_number}</span>
                    <StatusBadge status={est.status} />
                    {est.converted_job_id && <Badge variant="outline" className="text-[9px]">Job</Badge>}
                  </div>
                  <TruncatedTextWithTooltip
                    as="p"
                    text={est.customer_name || 'No customer'}
                    className="text-xs text-muted-foreground"
                  />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-bold">${est.total.toFixed(2)}</span>
                  {canEdit && (
                    <button
                      onClick={(e) => handleDeleteClick(e, est)}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      data-testid={`delete-estimate-btn-${est.id}`}
                      title={est.converted_job_id ? 'Cannot delete — converted to job' : 'Delete estimate'}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="border rounded-lg hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="number" onSort={handleSort}>Details</SortableTableHead>
                <TableHead className="w-16 text-center hidden sm:table-cell">Items</TableHead>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="status" onSort={handleSort} className="w-32 hidden md:table-cell">Status</SortableTableHead>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="total" onSort={handleSort} className="w-28 text-right hidden sm:table-cell">Total</SortableTableHead>
                <SortableTableHead sortKey={sortBy} sortDir={sortDir} columnKey="created" onSort={handleSort} className="w-24 text-right hidden lg:table-cell">Created</SortableTableHead>
                {canEdit && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : estimates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="w-10 h-10 text-muted-foreground/40" />
                      <p className="text-sm font-medium">{search || statusFilter ? 'No estimates found' : 'No estimates yet'}</p>
                      <p className="text-xs text-muted-foreground">{search || statusFilter ? 'Try adjusting your filters.' : 'Create your first estimate to get started.'}</p>
                      {!search && !statusFilter && canEdit && (
                        <Button size="sm" className="mt-2" onClick={() => navigate('/estimates/new')} data-testid="create-estimate-empty-btn"><Plus className="w-4 h-4 mr-1" /> New Estimate</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                estimates.map(est => (
                  <TableRow
                    key={est.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/estimates/${est.id}`)}
                    data-testid={`estimate-row-${est.id}`}
                  >
                    <TableCell>
                      <div className="flex items-baseline gap-2">
                        <span className="text-base font-semibold" data-testid={`estimate-number-${est.id}`}>{est.estimate_number}</span>
                        {est.converted_job_id && <Badge variant="outline" className="text-[9px]">Job</Badge>}
                        <span className="sm:hidden"><StatusBadge status={est.status} /></span>
                      </div>
                      {(() => {
                        const parts = [
                          est.customer_name || null,
                          est.employees.length > 0 ? est.employees.join(', ') : null,
                        ].filter(Boolean);
                        return parts.length > 0 ? (
                          <div className="text-xs text-muted-foreground mt-0.5">{parts.join(' · ')}</div>
                        ) : (
                          <div className="text-xs text-muted-foreground mt-0.5 italic">No customer</div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-center hidden sm:table-cell">
                      <span className="text-sm text-muted-foreground">{est.items_count}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <StatusBadge status={est.status} />
                    </TableCell>
                    <TableCell className="text-right hidden sm:table-cell">
                      <span className="text-sm font-medium">${est.total.toFixed(2)}</span>
                    </TableCell>
                    <TableCell className="text-right hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">
                        {est.created_at ? new Date(est.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                      </span>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-center">
                        <button
                          onClick={(e) => handleDeleteClick(e, est)}
                          className="p-1.5 rounded-md text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10 transition-colors"
                          data-testid={`delete-estimate-btn-${est.id}`}
                          title={est.converted_job_id ? 'Cannot delete — converted to job' : 'Delete estimate'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {totalPages > 1 && (
          <ListPagination 
            page={page} 
            pageSize={pageSize} 
            total={total} 
            totalPages={totalPages} 
            showPageSize={false}
            onPageChange={(p) => {
              const params = new URLSearchParams(searchParams);
              params.set('page', String(p));
              setSearchParams(params);
            }} 
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-estimate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Estimate {deleteTarget?.estimate_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the estimate
              {deleteTarget?.customer_name ? ` for ${deleteTarget.customer_name}` : ''}
              {deleteTarget?.total ? ` ($${deleteTarget.total.toFixed(2)})` : ''}
              , including all line items, options, and attachments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-estimate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-estimate-confirm"
            >
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : 'Delete Estimate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {canEdit && <MobileFAB onClick={() => navigate('/estimates/new')} data-testid="mobile-fab-estimate" />}
    </Layout>
  );
}
